/**
 * THE TONE SEA'S FIELD (CONTRACT 1.5w, `?water=render`). Pure functions of a world point, the
 * wave-frame tick, the shelf distance the caller looked up, and the GUESSes in `WATER_SEA`.
 * Nothing here reads a clock, holds a counter, or keeps a seed. Presence and determinism are
 * properties of the signatures below, not of a discipline someone has to remember.
 *
 * -- WHAT THE RENDERS ACTUALLY SHOW, and what that forces ---------------------------------------
 *
 * Broad FLAT tone regions with long irregular boundaries; a shelf that lightens towards land; a
 * sparse network of thin pale streaks that FORK. Three consequences, each of which is why one
 * piece below exists rather than a simpler one:
 *
 *   THE DOMAIN WARP IS NOT POLISH. Iso-lines of an unwarped fbm are smooth blobs and read as
 *   contour rings the moment you quantise them to seven hard values, which is 1.5e's whole risk
 *   in this round (dithering, alpha ramps and shader lerps are all forbidden, so there is no
 *   later fix for banding). Warping the domain by ~40 px of a two-octave field turns every
 *   boundary into a long irregular blotch edge. `valueNoise3` quantises to blobby cloud, which is
 *   why the mottle is ANISOTROPIC - stretched 3:1 along the swell diagonal - and the renders'
 *   boundaries are long and directional, not round.
 *
 *   THERE IS NO DRIFT TERM. A drifting quantised field flips large contiguous regions by one
 *   index every 190 ms: a low-frequency area flicker that no reference STILL can warn you about
 *   and that 1.5i(d) ("It's dizzying") already ruled on once. The tone field is FROZEN IN WORLD
 *   SPACE. What moves is the crest, by an integer offset that closes its loop.
 *
 *   THE CREST IS A SECOND MARCHING-SQUARES BAND, not stamped sprites. Iso-contours of a warped
 *   fbm FORK - the Y-junctions plainly visible in blackstage-8325 and woolstrand-8302 - and
 *   independent stamped sprites provably cannot make one. Its threshold is
 *   `crestGain * calmAmplitude`, so CALM MOVES COVERAGE rather than contrast, and the lift moves
 *   with the same scalar (palette.ts `crestFor`), which is what keeps the whole layer inside the
 *   one number `water.test.ts:639` and `arrival.mjs` already measure.
 *
 * -- THE SEAM, AND WHY IT CANNOT BE HASHED ------------------------------------------------------
 *
 * A boundary cell draws one hard-alpha marching-squares overlay. The crossing on each edge is
 * interpolated from the TWO CORNERS BOTH NEIGHBOURS SHARE and quantised to EDGE_QUANT positions,
 * so adjacent cells compute the identical crossing BY CONSTRUCTION - not to a tolerance, and not
 * by agreeing to hash the same thing. `sea.test.ts` walks over 40,000 shared edges of a 512x512
 * field and asserts exact equality, including on whether there is a crossing at all.
 *
 * And the threshold is a property of the BAND LEVEL, never of the cell: a cell whose corners span
 * three bands emits one overlay per level it spans, in increasing order. The alternative - one
 * overlay per cell at the cell's own minimum band - makes two neighbours disagree about which
 * threshold they are drawing, and that disagreement IS the seam. It costs an overlay on the rare
 * three-band cell and it removes a whole class of artefact.
 */

import { hash32, unit } from './hash';
import { calmAmplitude, type CalmScene, type WaterCalm, type WaterSea } from './water';

/** The tone lattice, world art px. Shares WATER_CELL_PX's grid so both arms are measured alike. */
export const SEA_CELL_PX = 16;
/** The crest lattice. Finer, because the renders' streaks are 4.6 to 8.7 art px long (measure.json). */
export const SEA_CREST_CELL_PX = 8;
/** Crossing positions per cell edge. 4 puts a boundary vertex every 4 art px on the tone lattice. */
export const EDGE_QUANT = 4;
/**
 * The crest's stroke, art px, and the DEFAULT of `WaterSea.crestStrokePx` (`?creststroke`).
 *
 * MEASURED reference thickness medians are 1.1, 2.0 and 2.9 art px
 * (gauntlet/living-sea/render-target-20260910/measure.json, `blobThicknessMedianArtPx` over the
 * three references' shelf rectangles). 2 is the middle of those three and was the GUESS taken;
 * the render the owner points at most is the 2.9. The game's own render arm measures 3.1 at
 * Murrin Roost's shelf (likeness-murrin-roost-render.json) - a stamped 2 px stroke thickens where
 * two bank cases meet - so this number is the PEN, not the thickness that comes out.
 *
 * It is not rounded to whole pixels. `buildCrestBank` fills where `distToSegment <= strokePx / 2`,
 * so 2.9 is a real and different bank from 2 and from 3: the half-width lands between the pixel
 * centres and the stroke is 3 px wide along the run and 2 px at the shallowest diagonals.
 */
export const CREST_STROKE_PX = 2;
/**
 * The widest stroke the bank can hold. A stroke is stamped inside ONE `SEA_CREST_CELL_PX` cell
 * and clipped by it, so past the cell the extra width is thrown away and every case fills solid:
 * the crest stops being a network and becomes a flood. The clamp is here, beside the cell it is a
 * property of, rather than as a literal in world-entry.ts.
 */
export const CREST_STROKE_MAX_PX = SEA_CREST_CELL_PX;

const smooth = (t: number): number => t * t * (3 - 2 * t);
const smoothstep01 = (t: number): number => (t <= 0 ? 0 : t >= 1 ? 1 : smooth(t));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * TWO-DIMENSIONAL value noise on an integer lattice, smoothstep-interpolated, in [0, 1]. Four
 * hashes, against `valueNoise3`'s eight, and this field has no time axis to spend the other four
 * on. Stretched x2 about 0.5 and clamped for the reason `valueNoise3` is: bilinear blending of
 * four uniforms has half their contrast and every patch would come out middling.
 */
export function valueNoise2(u: number, v: number, salt: number): number {
  const iu = Math.floor(u);
  const iv = Math.floor(v);
  const fu = smooth(u - iu);
  const fv = smooth(v - iv);
  const c = (a: number, b: number): number => unit(hash32(iu + a, iv + b, salt, 0x5ea));
  const n = lerp(lerp(c(0, 0), c(1, 0), fu), lerp(c(0, 1), c(1, 1), fu), fv);
  return Math.min(1, Math.max(0, (n - 0.5) * 2 + 0.5));
}

const SALT_WARP_X = 0x11;
const SALT_WARP_Y = 0x12;
const SALT_MOTTLE = 0x13;
const SALT_CREST = 0x14;
const SALT_FOAM = 0x15;
const SALT_SHORE_BAND = 0x16;

/** GUESS: the warp's second octave, non-harmonic so the two never line up into a lattice. */
export const WARP_OCTAVE_2 = 2.45;

/**
 * THE DOMAIN WARP at a world point: how far to move a sample before the tone field reads it.
 * Two octaves, each axis on its own salt. Frozen in world space, so it is the same warp on
 * every reload and in every tab and at every draw rate.
 */
export function warpAt(wx: number, wy: number, sea: WaterSea): readonly [number, number] {
  const out = new Float64Array(2);
  warpInto(wx, wy, sea, out, 0);
  return [out[0]!, out[1]!];
}

/**
 * The same, written into a caller's buffer at `at` and `at + 1`. The renderer takes 2,135 of
 * these a draw and a two-element array each would be 2,135 allocations per frame; `warpAt` above
 * is the readable form and every test uses it.
 */
export function warpInto(wx: number, wy: number, sea: WaterSea, out: Float32Array | Float64Array, at: number): void {
  const L1 = sea.warpPx;
  const L2 = sea.warpPx / WARP_OCTAVE_2;
  const dx = (valueNoise2(wx / L1, wy / L1, SALT_WARP_X) - 0.5) * 2 * 0.7 + (valueNoise2(wx / L2, wy / L2, SALT_WARP_X + 1) - 0.5) * 2 * 0.3;
  const dy = (valueNoise2(wx / L1, wy / L1, SALT_WARP_Y) - 0.5) * 2 * 0.7 + (valueNoise2(wx / L2, wy / L2, SALT_WARP_Y + 1) - 0.5) * 2 * 0.3;
  out[at] = wx + dx * sea.warpAmp;
  out[at + 1] = wy + dy * sea.warpAmp;
}

/** GUESS: the mottle's three non-harmonic octaves and their weights. */
const MOTTLE_OCTAVES: readonly (readonly [number, number])[] = [
  [1, 0.55],
  [1.83, 0.3],
  [3.31, 0.15],
];
/** GUESS: how far the mottle is stretched along the swell diagonal. The renders' blotches are long. */
export const MOTTLE_STRETCH = 3;

/**
 * THE MOTTLE at an ALREADY-WARPED point, in [-1, 1]: what moves the open sea off one flat value.
 * Rotated onto the swell diagonal and stretched MOTTLE_STRETCH:1 along it, because an isotropic
 * field quantises to round cloud and the renders' tone regions are long and directional.
 */
export function mottleAt(px: number, py: number, sea: WaterSea): number {
  const u = (px + py) * 0.70710678;
  const v = (px - py) * 0.70710678;
  let acc = 0;
  let wsum = 0;
  for (const [f, w] of MOTTLE_OCTAVES) {
    const L = sea.mottlePx / f;
    acc += valueNoise2(u / (L * MOTTLE_STRETCH), v / L, SALT_MOTTLE + f * 7) * w;
    wsum += w;
  }
  return (acc / wsum - 0.5) * 2;
}

/**
 * WHERE ON THE TONE RAMP a world point sits, 0 deepest .. 1 palest.
 *
 * `shelfDistPx` is the caller's business: it is the distance to the nearest island SILHOUETTE
 * (shelf.ts), minimised over the sprites on screen, and Infinity in open water. Keeping it a
 * parameter is what lets this file be pure and testable without a sprite.
 */
export function toneT(wx: number, wy: number, shelfDistPx: number, sea: WaterSea): number {
  const [px, py] = warpAt(wx, wy, sea);
  return toneTWarped(px, py, shelfDistPx, sea);
}

/**
 * THE SAME, given the ALREADY-WARPED point. The renderer uses this one, because the shelf
 * distance has to be looked up AT THE WARPED POINT or the shelf's own iso-lines stay smooth.
 *
 * THAT IS NOT A DETAIL AND IT WAS A VISIBLE FAILURE FIRST. Looking the shelf up at the true
 * point and mottling only afterwards drew Murrin Roost's bay as a bathymetric map: five
 * concentric hard bands parallel to the shore, because near a shore the shelf term dominates the
 * mottle and it is far smoother. Warping the LOOKUP moves the whole shelf boundary by up to
 * warpAmp px in an irregular, long, directional way - which is what the domain warp is for.
 */
export function toneTWarped(px: number, py: number, shelfDistPx: number, sea: WaterSea): number {
  const shelfT = shelfDistPx >= sea.shelfPx ? 0 : 1 - smoothstep01(shelfDistPx / sea.shelfPx);
  const t = sea.deep + (1 - sea.deep) * shelfT + sea.mottle * mottleAt(px, py, sea);
  return t <= 0 ? 0 : t >= 1 ? 1 : t;
}

/** The band a ramp position lands on: 0 .. tones-1. */
export function bandOf(t: number, tones: number): number {
  const b = Math.round(Math.min(1, Math.max(0, t)) * (tones - 1));
  return b < 0 ? 0 : b > tones - 1 ? tones - 1 : b;
}

/** The ramp position at which band `m` gives way to band `m + 1`. */
export function bandThreshold(m: number, tones: number): number {
  return (m + 0.5) / (tones - 1);
}

/**
 * WHERE ON AN EDGE the field crosses a threshold: 0 .. EDGE_QUANT-1, or -1 for no crossing.
 *
 * `ta` and `tb` are the two corner values IN CANONICAL ORDER - a is the corner with the smaller
 * lattice index, which is the left one on a horizontal edge and the top one on a vertical edge.
 * Both cells that share an edge pass the same two floats and the same threshold, so both get the
 * same integer. That is the seam guarantee, and it is structural rather than tolerant.
 */
export function edgeCrossing(ta: number, tb: number, thr: number): number {
  const a = ta > thr;
  const b = tb > thr;
  if (a === b) return -1;
  const d = tb - ta;
  const u = d === 0 ? 0.5 : (thr - ta) / d;
  const q = Math.floor(u * EDGE_QUANT);
  return q < 0 ? 0 : q > EDGE_QUANT - 1 ? EDGE_QUANT - 1 : q;
}

/**
 * THE FOUR EDGE CROSSINGS OF ONE CELL, in edge order (top, right, bottom, left), each -1 for no
 * crossing. THIS IS THE ONLY PLACE THE CANONICAL ORDER IS APPLIED, and it is the seam guarantee
 * in one function: the top edge is read left to right, the right edge top to bottom, the bottom
 * edge LEFT TO RIGHT (not right to left, which is the direction the boundary walk goes) and the
 * left edge top to bottom. A cell's right edge and its right neighbour's left edge are then the
 * same two corner values passed the same way round, so the two integers are equal by construction
 * rather than by tolerance. The renderer and `sea.test.ts` both call this and nothing else.
 */
export function cellEdgeCrossings(tTL: number, tTR: number, tBR: number, tBL: number, thr: number, out?: Int32Array): Int32Array {
  const o = out ?? new Int32Array(4);
  o[0] = edgeCrossing(tTL, tTR, thr);
  o[1] = edgeCrossing(tTR, tBR, thr);
  o[2] = edgeCrossing(tBL, tBR, thr);
  o[3] = edgeCrossing(tTL, tBL, thr);
  return o;
}

/**
 * The marching-squares case of a cell at a threshold: bit 0 top-left, 1 top-right, 2 bottom-right,
 * 3 bottom-left, set where the field is ABOVE the threshold. 0 and 15 never reach the bank -
 * a cell with all four corners on one side of a threshold does not span that band level at all.
 */
export function marchCase(tTL: number, tTR: number, tBR: number, tBL: number, thr: number): number {
  return (tTL > thr ? 1 : 0) | (tTR > thr ? 2 : 0) | (tBR > thr ? 4 : 0) | (tBL > thr ? 8 : 0);
}

// ---- the crest --------------------------------------------------------------------------------

/**
 * THE CREST'S LOOP, as an integer offset in art px applied to the field's sample position.
 *
 * A triangle wave along the swell diagonal, four px out and four back over sixteen frames. It is
 * INTEGER, so the crest network never lands between pixels, and its per-transition steps sum to
 * exactly (0, 0) around the loop - the bank instrument asserts that, and if it were not true the
 * sea would walk a little further across the world every sixteen frames for as long as a tab was
 * left open, which is exactly the sort of accrued state the presence law forbids.
 */
/**
 * THE CREST FIELD DEPENDS ON THE FRAME ONLY THROUGH THIS OFFSET, and the offset repeats: frames
 * 1 and 7 are both (1, -1), 2 and 6 are both (2, -2), and so on. Sixteen frames carry NINE
 * distinct offsets, so a memo keyed on the offset misses nine times a loop where one keyed on the
 * frame misses sixteen. `crestFrameKey` is that key, and it is exact rather than approximate:
 * two frames with the same offset draw the same crest field by definition of `crestField`.
 */
export function crestFrameKey(waterFrame: number): number {
  const sw = STREAK_SWING[((waterFrame % STREAK_SWING.length) + STREAK_SWING.length) % STREAK_SWING.length]!;
  return sw[0] * 32 + sw[1];
}

export const STREAK_SWING: readonly (readonly [number, number])[] = [
  [0, 0], [1, -1], [2, -2], [3, -3], [4, -4], [3, -3], [2, -2], [1, -1],
  [0, 0], [-1, 1], [-2, 2], [-3, 3], [-4, 4], [-3, 3], [-2, 2], [-1, 1],
];

/**
 * THE CREST FIELD at a world point on a wave frame, in [0, 1]. Two anisotropic octaves on the
 * warped domain, so its iso-contours are long, irregular and FORK. The frame's integer offset is
 * added to the sample position, which is the whole of the crest's motion.
 */
export function crestField(px: number, py: number, waterFrame: number, sea: WaterSea): number {
  const sw = STREAK_SWING[((waterFrame % STREAK_SWING.length) + STREAK_SWING.length) % STREAK_SWING.length]!;
  const x = px + sw[0];
  const y = py + sw[1];
  const u = (x + y) * 0.70710678;
  const v = (x - y) * 0.70710678;
  const L = sea.crestPx;
  // LESS ANISOTROPY THAN THE FIRST VERSION'S 2.2 : 1 and 0.9 : 0.41. LOOKED AT, torsk-vard at 1x:
  // at those ratios every contour ran the same diagonal and the sea read as COMBED HAIR - long
  // near-parallel strokes. woolstrand-8302 at 3x is a WEB: the lines fork, close into irregular
  // cells and change direction. An iso-contour inherits the field's anisotropy directly, so the
  // two octaves are stretched only enough to keep the swell's direction legible.
  const a = valueNoise2(u / (L * 1.8), v / (L * 0.9), SALT_CREST);
  const b = valueNoise2(u / (L * 0.75), v / (L * 0.45), SALT_CREST + 1);
  return a * 0.65 + b * 0.35;
}

/**
 * THE CREST'S THRESHOLD at a world point: the field value above which a crest is drawn.
 *
 * CALM MOVES COVERAGE, NOT CONTRAST. `calmAmplitude` is 1 at the boat and at a shore and falls to
 * the calm floor on the open sea, so this rises as the water quietens and the iso-contour retreats
 * to the field's few remaining peaks. At amplitude 0 the threshold is 1 and there is no crest at
 * all. The measured correction the round was given is that amplitude must scale the COVERAGE and
 * not the step; the step moves too (palette.ts `crestFor`), but only by 20..50 luma.
 */
export function crestThreshold(wx: number, wy: number, tickMs: number, scene: CalmScene, calm: WaterCalm, sea: WaterSea): number {
  return 1 - crestCoverageOf(calmAmplitude(wx, wy, tickMs, scene, calm), sea);
}

/**
 * WHAT FRACTION OF THE FIELD THE CREST MAY REACH at an amplitude. ONE function, because the
 * renderer computes this inline on its own lattice (world-view.ts, interpolated amplitude) and
 * `arrival.mjs` computes it again to report `pctCrestCoverage`; three copies of an expression is
 * three chances for the gate and the picture to disagree about the sea.
 */
export function crestCoverageOf(amplitude: number, sea: WaterSea): number {
  const c = sea.crestGain * CREST_COVERAGE * Math.pow(Math.max(0, amplitude), CREST_AMP_EXP);
  return c <= 0 ? 0 : c >= 1 ? 1 : c;
}

/**
 * THE AMPLITUDE'S EXPONENT, AND WHY IT IS NOT 1 - a correction the first shipped crest needed.
 *
 * CONTOUR LENGTH IS NOT MONOTONE IN COVERAGE. What is drawn is the ISO-CONTOUR of the crest
 * field, and a contour is longest when its threshold sits near the field's median and vanishes at
 * both ends. The open sea's own amplitude is the calm floor plus the gust, 0.20 to 0.45, so a
 * linear map put the open sea's threshold at 0.856 of a field whose median is 0.5 - about two
 * standard deviations out - and MEASURED, `likeness-murrin-roost-render.json` regions.open came
 * back with `blobs: 0` and `brightFractionPct: 0`. The open sea had NO CREST IN IT AT ALL, which
 * is the one thing woolstrand-8302 - the render the owner named first - is mostly made of.
 *
 * Raising CREST_COVERAGE instead would push the SHORE end past the median and shorten the contour
 * there, so the same knob would have to go both ways at once. The exponent is the shape that
 * fixes the open end without moving the shore end past 0.5:
 *
 *   amplitude   0     0.20    0.30    0.45    1.00
 *   threshold   1.0   0.776   0.726   0.665   0.500
 *
 * The law is unchanged and the test that guards it is unchanged: at amplitude 0 the coverage is
 * still exactly 0 and the threshold is still exactly 1.
 */
export const CREST_AMP_EXP = 0.5;

/**
 * GUESS: the fraction of the field the crest can reach at full amplitude and crestGain 1. The
 * measured reference bright fractions over p50+60 are 0.0%, 1.09% and 3.19% in open water. With
 * CREST_AMP_EXP below, this is the coverage at FULL amplitude - at a shore and at the boat - and
 * 0.5 puts that threshold exactly on the field's median, which is where an iso-contour is
 * longest. Everything quieter than that is shorter, so contour length is monotone in the calm
 * across the whole range the calm field actually uses.
 */
export const CREST_COVERAGE = 0.5;

/** Is a crest drawn at this field value against this threshold? */
export function crestBandOf(f: number, threshold: number): boolean {
  return f > threshold;
}

// ---- THE CREST ANSWERS THE LAND (CONTRACT 1.5y) -----------------------------------------------

/**
 * HOW MUCH THE SHORE GOVERNS THE CREST at a shelf distance: 1 at the waterline, 0 at
 * `crestShorePx` and beyond.
 *
 * -- THE DEFECT THIS EXISTS TO ANSWER, IN THE ROUND'S OWN NUMBERS -------------------------------
 *
 * The owner, on eight in-game sea variants: *"It should react to the islands and it should be
 * more refined than random lines"*. The tone shelf already read the shore; the crest never did.
 * MEASURED on the shipped render arm, `crestPct` - the share of a rectangle carrying an exact
 * emitted crest colour - shelf against open water in the SAME frame:
 *
 *   Murrin Roost   shelf 2.090   open 2.498   ratio 0.84   (living-sea-r9/likeness-murrin-roost-render.json)
 *   Torsk Vard     shelf 0.993   open 1.593   ratio 0.62   (living-sea-r9/likeness-torsk-vard-render.json)
 *
 * i.e. the crest was very slightly DENSER six hundred metres out than it was against a cliff.
 * MEASURED on the three renders the owner said they loved, the same ratio on their own bright
 * fraction (`gauntlet/living-sea/render-target-20260910/measure.json`): 8.288 / 0.000 = infinite,
 * 13.848 / 3.191 = 4.34, and 0.046 / 1.091 = 0.04. In two of the three the crest belongs to the
 * SHORE by four times or more.
 *
 * `Infinity` and `NaN` both fall out as 0 through the negated comparison, which is the case that
 * matters most: open water is `Infinity` and is the quiet case.
 */
export function shorenessAt(shelfDistPx: number, sea: WaterSea): number {
  if (!(shelfDistPx < sea.crestShorePx)) return 0;
  const hold = sea.crestShorePx * CREST_SHORE_HOLD;
  const d = Math.max(0, shelfDistPx);
  if (d <= hold) return 1;
  return 1 - smoothstep01((d - hold) / (sea.crestShorePx - hold));
}

/**
 * GUESS: the share of the crest's shore reach over which the shore governs COMPLETELY, before the
 * falloff starts. A bare smoothstep from the waterline gives a shore only half its say at half
 * its reach, and MEASURED that is where the water this ruling is about actually is: the likeness
 * instrument's shelf rectangle at Torsk Vard runs 1 to 85 art px from land with a median of 42.8,
 * so under a bare smoothstep at reach 96 its median pixel was 0.57 governed by the shore and 0.43
 * by the open sea, and the marks there came out half-turned. The plateau makes the band a BAND -
 * fully the shore's out to 63 px, gone by 140 - which is what "marks gather in a band off a
 * coast" says.
 */
export const CREST_SHORE_HOLD = 0.45;

/**
 * THE CREST'S COVERAGE AT A POINT: the calm's own coverage, THINNED INTO OPEN WATER.
 *
 * `crestCoverageOf` is what the calm alone asks for and it is unchanged - 1.5i(d)'s bar is still
 * measured through it. This multiplies it by the shore's own scalar, which runs from
 * `sea.crestOpen` far from any land to 1 at a waterline. It is a MULTIPLIER and not a second
 * ramp so that amplitude 0 still gives coverage exactly 0: the calm law is unchanged.
 *
 * WHY THINNING WORKS ON THE PICTURE and not only on the number: what is drawn is the ISO-CONTOUR
 * of the crest field, and a contour is longest at the field's median and vanishes into its tails.
 * The open sea's coverage falls from ~0.22..0.33 to ~0.10..0.15, which moves its threshold from
 * near the median out to the top decile, so the open-water network stops being a network and
 * becomes a few short separate marks - which is the second half of the ruling as well as the
 * first.
 */
export function crestCoverageAt(amplitude: number, shoreness: number, sea: WaterSea): number {
  const s = shoreness <= 0 ? 0 : shoreness >= 1 ? 1 : shoreness;
  const c = crestCoverageOf(amplitude, sea) * (sea.crestOpen + (1 - sea.crestOpen) * s);
  return c <= 0 ? 0 : c >= 1 ? 1 : c;
}

/**
 * THE ALONG-COAST LATTICE of the band's wander, as a multiple of `crestBandPx`. At 48 and an 8 px
 * band that is one wander cell per 384 art px of coastline - the band holds its distance for
 * about a third of a frame's width before it starts to move in or out.
 *
 * THIS RATIO IS THE ALIGNMENT, and it is the one number the second half of 1.5y turns on. Per art
 * px of coast the along-coordinate moves by SPREAD / (crestBandPx * ALONG); per art px out from
 * the shore the across-coordinate moves by 1 / crestBandPx. Their ratio, ALONG / SPREAD, is how
 * many times more elongated a contour is along the coast than across it. MEASURED at Murrin
 * Roost's shelf over eight wave frames, mean cos(2 dtheta) of the marks against the local shore
 * tangent, at crestBandPx 14 and 9 respectively:
 *
 *   ALONG / SPREAD    4 / 5 = 0.8     10 / 2 = 5      12 / 1 = 12     48 / 1.6 = 30
 *   alignment         0.3252          0.5085          0.8654          0.8506
 *
 * Below about 5 the contours are as likely to run across the shore as along it, which is what the
 * first version shipped and what the pictures showed as hooks and chevrons.
 */
export const CREST_BAND_ALONG = 48;
/**
 * GUESS: how many band cells the wander sweeps across as its own field runs 0 to 1.
 *
 * IT IS NOT 1, AND THE PICTURE IS WHY. At 1 the along-field only ever samples a single cell of
 * its own noise, so a band holds ONE distance off the coast for as far as the eye follows it and
 * the sea reads as a CONTOUR MAP - concentric rings at a fixed offset, which is a different wrong
 * answer from the random lines this ruling rejected, not a right one. LOOKED AT, Torsk Vard's bay
 * at 1x: three to five evenly spaced parallel arcs following the shore. At 1.6 the bands move in
 * and out along the coast and cross each other's offsets; MEASURED at the same camera, the shelf
 * mark density rises from 1.71% to 5.17% (the references' own shelves are 8.288% and 13.848%) and
 * the alignment falls from 0.9221 to 0.8506, still over woolstrand-8302's 0.8416.
 */
export const CREST_BAND_SPREAD = 1.6;

/**
 * THE SHORE BAND at a TRUE world point, in [0, 1]: the field whose iso-contours lie ALONG a coast
 * rather than across it.
 *
 * IT IS NOT A PERIODIC RIPPLE, AND THAT IS HALF THE DESIGN. The obvious shore-parallel field is
 * `cos(2 pi d / period)`, whose level sets are exact offsets of the coastline - and a periodic
 * field is a LATTICE. `sea-likeness.mjs`, `measure.py` and `sea.test.ts` all gate on the same
 * three-part autocorrelation rule, and round 4's sea was rejected by the owner for being regular
 * along an axis; a ruled set of surf lines every 24 px would be that rejection again with a
 * different generator. So the band is VALUE NOISE READ IN SHORE COORDINATES:
 *
 *   across   the shelf distance itself, in units of `crestBandPx`
 *   along    a slow field sampled AT THE FOOT POINT - the nearest land pixel this water is
 *            measured to, `(wx, wy) + d * (nx, ny)`
 *
 * THE FOOT POINT IS THE OTHER HALF, AND THE FIRST VERSION GOT IT WRONG - MEASURED. That version
 * projected the WATER point on the shore tangent. A world coordinate here is in the thousands and
 * the tangent rotates from one lattice corner to the next along a rocky coast, so a 0.05 turn of
 * the normal moved the along-coordinate by half a lattice cell: the along-coordinate was noise,
 * and the marks came out as hooks and chevrons pointing every way. LOOKED AT, Torsk Vard's shelf
 * at 4x: little 7s and checkmarks, and the alignment measure read -0.2568, i.e. slightly ACROSS
 * the shore. The foot point fixes it structurally rather than by tuning: every water point on one
 * normal ray has the SAME foot point, so `along` is constant across the ray and the band's
 * contours are parallel to the coast by construction, while the foot point slides smoothly along
 * the coast as the water point does.
 *
 * THE FRAME'S MOTION IS THE SWING PROJECTED ON THE NORMAL, so the band breathes in and out from
 * the land rather than sliding along it, and because `STREAK_SWING` sums to (0, 0) around the
 * sixteen frames the band returns to exactly where it started. `nx, ny` is the unit vector from
 * the water towards the land (shelf.ts `shelfNormalAt`); a zero vector means no shore in reach
 * and the caller must not have got here, but it degrades to a plain field rather than a NaN.
 */
export function shoreBandAt(wx: number, wy: number, shelfDistPx: number, nx: number, ny: number, waterFrame: number, sea: WaterSea): number {
  const sw = STREAK_SWING[((waterFrame % STREAK_SWING.length) + STREAK_SWING.length) % STREAK_SWING.length]!;
  const across = (shelfDistPx + sw[0] * nx + sw[1] * ny) / sea.crestBandPx;
  const L = sea.crestBandPx * CREST_BAND_ALONG;
  const footX = wx + shelfDistPx * nx;
  const footY = wy + shelfDistPx * ny;
  // TWO TERMS, AND THE FIRST IS WHY THE SECOND IS NOT ENOUGH ON ITS OWN. A version with only the
  // wander had `along` confined to [0, CREST_BAND_SPREAD] - under two lattice cells - for the
  // whole world, so every coast in the sea sampled the same two columns of one noise field, and a
  // FENCE caught what looking had not: at 6 art px off a north-facing shore the band never rose
  // above its own threshold in 4,000 samples, because that one slice of those two cells happens
  // to be low. The linear projection of the foot point makes the along-coordinate unbounded, so
  // distant stretches of coast are unrelated; the wander keeps it from being a ruled function of
  // one direction where a coast happens to run square to that projection.
  const along = (footX * 0.8536 + footY * 0.5209) / L + valueNoise2(footX / L, footY / L, SALT_SHORE_BAND + 1) * CREST_BAND_SPREAD;
  return valueNoise2(along, across, SALT_SHORE_BAND);
}

/**
 * THE FIELD THE CREST IS MARCHED ON: the open sea's field, TURNED TOWARDS THE SHORE where there
 * is one. `shoreness` is `shorenessAt` at this point and `sea.crestBandWeight` is how far the
 * turn is allowed to go at a waterline.
 *
 * TWO POSITIONS, AND THEY ARE NOT THE SAME POINT. `px, py` is the WARPED sample position, which
 * is what the open sea's field has always been read at; `wx, wy` is the corner's TRUE world
 * position, which is what the shore band is read at. world-view.ts carries the measurement
 * behind that split: a 40 px domain warp is three of the shore band's 14 px and destroys the
 * across-shore coordinate the band is made of.
 *
 * THE BLEND IS WHAT BREAKS THE BANDS UP, and it is why the weight is not 1. At weight 0.85 the
 * shore band decides where a mark can be and the open field decides, along the mark's own length,
 * whether it clears the threshold there - so a run of surf comes out as separate oriented dashes
 * rather than one contour hugging the whole coast. The dash gate (`crestDashAt`) cuts them again
 * on its own lattice. "No contour that crosses the frame" needs both.
 */
export function crestFieldAt(px: number, py: number, wx: number, wy: number, shelfDistPx: number, nx: number, ny: number, shoreness: number, waterFrame: number, sea: WaterSea): number {
  const open = crestField(px, py, waterFrame, sea);
  const w = (shoreness <= 0 ? 0 : shoreness >= 1 ? 1 : shoreness) * sea.crestBandWeight;
  if (w <= 0) return open;
  return open * (1 - w) + shoreBandAt(wx, wy, shelfDistPx, nx, ny, waterFrame, sea) * w;
}

/** GUESS: the dash gate's lattice in world art px, and the fraction of it that is ON. */
export const CREST_DASH_PX = 10;
/**
 * 0.62, not the 0.34 the first crest shipped. LOOKED AT, `murrin-roost-render.png` at 3x: at 0.34
 * the crest was a dozen isolated 2 px curves reading as stray hairs on a flat tone, and the
 * reference it is drawn from - woolstrand-8302, at 3x beside it - is a CONNECTED WHITE NETWORK
 * with Y-junctions, covering 5.64% of its own crop. A gate that passes a third of the cells cuts
 * a network into unrecognisable pieces; one that passes about two thirds leaves the network
 * legible and still breaks the single frame-wide contour that made the round add a gate at all.
 */
export const CREST_DASH_ON = 0.45;

/**
 * THE DASH GATE: whether a crest cell draws at all.
 *
 * WITHOUT IT THE CREST IS ONE UNBROKEN CURVE, and that was the first thing wrong with the first
 * crest drawn (work/step7-render.png): a single iso-contour meandering the whole width of the
 * frame and reading as a stray hair, where the references' ripple lines are SHORT - measure.json
 * puts their blob length medians at 4.6, 6.4 and 8.7 art px. An iso-contour of a continuous field
 * is a closed curve and cannot be short; what makes a dash is a second, higher-frequency field
 * cutting it. Pure in the cell's world position, so two neighbours never disagree about a cell.
 */
export function crestDashAt(wx: number, wy: number, on: number = CREST_DASH_ON): boolean {
  const u = (wx + wy) * 0.70710678;
  const v = (wx - wy) * 0.70710678;
  return valueNoise2(u / (CREST_DASH_PX * 1.4), v / CREST_DASH_PX, SALT_CREST + 2) < on;
}

/**
 * HOW MUCH OF THE DASH GATE IS OPEN at a shoreness: `sea.crestDashOpen` far from any land, rising
 * to `CREST_DASH_ON` at a waterline.
 *
 * -- WHY THINNING THE COVERAGE ALONE COULD NOT DO THIS, MEASURED --------------------------------
 *
 * `crestCoverageAt` thins open water by raising the crest's threshold, and a threshold has a
 * FLOOR it cannot get past. `valueNoise2` stretches its bilinear blend x2 about 0.5 and CLAMPS,
 * so a real share of every sample lands at exactly 1.0 - a point mass at the top of the field
 * that no threshold below 1 can exclude. MEASURED at Torsk Vard's open rectangle over six wave
 * frames, mark density against `crestOpen`:
 *
 *   crestOpen   0.22    0.28    0.30    0.35
 *   markPct     1.126   1.190   1.199   1.221
 *
 * A sixty per cent change in the coverage moved the density by eight per cent. That is the point
 * mass, not the sea.
 *
 * A GATE HAS NO SUCH FLOOR: it decides whether a cell draws at all, so halving it halves the
 * marks. The gate is also the right place for the ruling's second half - it is what makes the
 * marks SHORT - and thinning it offshore is the same statement as "and thin out into open water".
 */
export function crestDashOnAt(shoreness: number, sea: WaterSea): number {
  const s = shoreness <= 0 ? 0 : shoreness >= 1 ? 1 : shoreness;
  return sea.crestDashOpen + (CREST_DASH_ON - sea.crestDashOpen) * s;
}

// ---- foam -------------------------------------------------------------------------------------

/**
 * THE FOAM'S OUTER EDGE, per column: 0 .. 1 of the material's full width.
 *
 * Hashed from the column, so the fringe is irregular rather than a ruled line, and the same
 * column always gets the same fringe. `key` is the sprite's own identity - a sprite's world
 * position is fixed for the life of the world, so hashing the sprite and the column is hashing
 * the world column, and it is the only one of the two that exists at the moment the band is
 * built. The boat, which is the one sprite that moves, never gets foam at all.
 */
export function foamAt(key: number, column: number): number {
  // THREE SCALES, AND THE PER-COLUMN ONE IS NEARLY NOTHING. LOOKED AT, murrin-roost at 3x with a
  // strong per-column term: the surf's outer edge came out a PICKET FENCE - 1 px teeth alternating
  // 2 to 4 px deep along the whole waterline, which is a comb and not a fringe. Surf breaks in
  // RUNS. So the swell is on groups of four and of sixteen columns, and the per-column jitter is
  // small enough (+-0.05 of the width, well under a pixel at any material) that it only ever
  // softens a boundary between two runs.
  const a = unit(hash32(key, column >> 2, SALT_FOAM, 3));
  const b = unit(hash32(key, column >> 4, SALT_FOAM, 5));
  const c = unit(hash32(key, column, SALT_FOAM, 7));
  return Math.min(1, Math.max(0, 0.5 + 0.34 * (b - 0.5) * 2 + 0.28 * (a - 0.5) * 2 + 0.05 * (c - 0.5) * 2));
}

// ---- THE BANKS: every marching-squares tile the sea can ask for, generated from code -----------

/**
 * THE FOUR EDGES of a cell, each named by the TWO CORNER BITS it joins, IN CANONICAL ORDER -
 * left before right, top before bottom. Both cells that share an edge index it the same way, and
 * that is what makes `edgeCrossing` return the same integer to both of them.
 *
 *   0  top     TL -> TR      bits 1, 2
 *   1  right   TR -> BR      bits 2, 4
 *   2  bottom  BL -> BR      bits 8, 4
 *   3  left    TL -> BL      bits 1, 8
 */
const EDGE_BITS: readonly (readonly [number, number])[] = [[1, 2], [2, 4], [8, 4], [1, 8]];

/** Which edges of a marching-squares case carry a crossing. Cases 0 and 15 carry none. */
export function edgesOfCase(c: number): readonly number[] {
  const out: number[] = [];
  for (let e = 0; e < 4; e++) {
    const [a, b] = EDGE_BITS[e]!;
    if (((c & a) !== 0) !== ((c & b) !== 0)) out.push(e);
  }
  return out;
}

/**
 * THE BANK'S LAYOUT, computed once from the cases themselves so no table can go stale. Twelve
 * cases carry two crossings (4^2 = 16 tiles each) and the two SADDLES carry four (4^4 = 256), so
 * a bank is 12 * 16 + 2 * 256 = 704 tiles. At the tone lattice's 16 px that is 720,896 B of
 * RGBA and at the crest lattice's 8 px it is 180,224 B - against the settled arm's MEASURED
 * 4,718,592 B of recoloured water sheets, which this arm never builds.
 */
export interface BankLayout {
  readonly count: number;
  /** First tile index of each case, indexed by case 0..15; -1 for the two that have no tiles. */
  readonly offset: readonly number[];
}

export function bankLayout(): BankLayout {
  const offset = new Array<number>(16).fill(-1);
  let n = 0;
  for (let c = 1; c <= 14; c++) {
    offset[c] = n;
    n += 4 ** edgesOfCase(c).length;
  }
  return { count: n, offset };
}

export const BANK = bankLayout();

/**
 * The bank index of one cell: its case, and the quantised crossing on each of the four edges
 * (-1 where that edge has none). Out-of-bank cases return -1 and draw nothing.
 */
export function bankIndexOf(c: number, q0: number, q1: number, q2: number, q3: number): number {
  const base = BANK.offset[c] ?? -1;
  if (base < 0) return -1;
  const q = [q0, q1, q2, q3];
  const edges = edgesOfCase(c);
  let v = 0;
  let mul = 1;
  for (const e of edges) {
    const qq = q[e]!;
    v += (qq < 0 ? 0 : qq > EDGE_QUANT - 1 ? EDGE_QUANT - 1 : qq) * mul;
    mul *= EDGE_QUANT;
  }
  return base + v;
}

/** The crossing point of edge `e` at quantised position `q`, in cell-local art px. */
function crossPoint(e: number, q: number, cell: number): readonly [number, number] {
  const p = ((q + 0.5) / EDGE_QUANT) * cell;
  if (e === 0) return [p, 0];
  if (e === 1) return [cell, p];
  if (e === 2) return [p, cell];
  return [0, p];
}

/** The bank tile's polygon: the cell boundary walked TL -> TR -> BR -> BL, upper corners kept. */
function casePolygon(c: number, q: readonly number[], cell: number): readonly (readonly [number, number])[] {
  const has = (e: number): boolean => {
    const [a, b] = EDGE_BITS[e]!;
    return ((c & a) !== 0) !== ((c & b) !== 0);
  };
  const v: (readonly [number, number])[] = [];
  if (c & 1) v.push([0, 0]);
  if (has(0)) v.push(crossPoint(0, q[0]!, cell));
  if (c & 2) v.push([cell, 0]);
  if (has(1)) v.push(crossPoint(1, q[1]!, cell));
  if (c & 4) v.push([cell, cell]);
  if (has(2)) v.push(crossPoint(2, q[2]!, cell));
  if (c & 8) v.push([0, cell]);
  if (has(3)) v.push(crossPoint(3, q[3]!, cell));
  return v;
}

function insidePolygon(poly: readonly (readonly [number, number])[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function distToSegment(x: number, y: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const L2 = dx * dx + dy * dy;
  const t = L2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - ax) * dx + (y - ay) * dy) / L2));
  return Math.hypot(x - (ax + t * dx), y - (ay + t * dy));
}

export interface Bank {
  readonly cell: number;
  readonly count: number;
  /** Tiles per row of the atlas. One image source; the batcher stays one draw call. */
  readonly cols: number;
  readonly w: number;
  readonly h: number;
  /** RGBA, white where drawn and alpha 0 everywhere else. Tinted at draw time. */
  readonly rgba: Uint8Array;
}

function emptyBank(cell: number): { cols: number; w: number; h: number; rgba: Uint8Array } {
  const cols = 32;
  const rows = Math.ceil(BANK.count / cols);
  const w = cols * cell;
  const h = rows * cell;
  return { cols, w, h, rgba: new Uint8Array(w * h * 4) };
}

function put(rgba: Uint8Array, w: number, x: number, y: number): void {
  const o = (y * w + x) * 4;
  rgba[o] = 255;
  rgba[o + 1] = 255;
  rgba[o + 2] = 255;
  rgba[o + 3] = 255;
}

/** Every variant's tile index and its quantised crossings, in bank order. */
function* variants(): Generator<{ index: number; c: number; q: number[] }> {
  for (let c = 1; c <= 14; c++) {
    const edges = edgesOfCase(c);
    const n = 4 ** edges.length;
    for (let v = 0; v < n; v++) {
      const q = [-1, -1, -1, -1];
      let rest = v;
      for (const e of edges) {
        q[e] = rest % EDGE_QUANT;
        rest = Math.floor(rest / EDGE_QUANT);
      }
      yield { index: bankIndexOf(c, q[0]!, q[1]!, q[2]!, q[3]!), c, q };
    }
  }
}

/**
 * THE BOUNDARY BANK: for each case and crossing set, the part of the cell that belongs to the
 * UPPER band, as hard alpha. Drawn over a run of the lower band and tinted to the upper tone,
 * which is an 8-bit multiply of white and therefore the tone exactly.
 */
export function buildEdgeBank(cell: number = SEA_CELL_PX): Bank {
  const { cols, w, h, rgba } = emptyBank(cell);
  for (const { index, c, q } of variants()) {
    const poly = casePolygon(c, q, cell);
    const tx = (index % cols) * cell;
    const ty = Math.floor(index / cols) * cell;
    for (let y = 0; y < cell; y++) {
      for (let x = 0; x < cell; x++) {
        if (insidePolygon(poly, x + 0.5, y + 0.5)) put(rgba, w, tx + x, ty + y);
      }
    }
  }
  return { cell, count: BANK.count, cols, w, h, rgba };
}

/**
 * THE CREST BANK: the same cases, but the CONTOUR rather than the region - one stroke joining the
 * crossings. The two saddle cases carry two strokes, which is where the network's junctions come
 * from: at a saddle the level set of a smooth field is a crossing, and a stamped sprite has no
 * way to be one.
 */
export function buildCrestBank(cell: number = SEA_CREST_CELL_PX, strokePx: number = CREST_STROKE_PX): Bank {
  const { cols, w, h, rgba } = emptyBank(cell);
  const r = strokePx / 2;
  for (const { index, c, q } of variants()) {
    const edges = edgesOfCase(c);
    const pairs: (readonly [number, number])[] = edges.length === 4 ? [[0, 1], [2, 3]] : [[edges[0]!, edges[1]!]];
    const tx = (index % cols) * cell;
    const ty = Math.floor(index / cols) * cell;
    for (const [ea, eb] of pairs) {
      const [ax, ay] = crossPoint(ea, q[ea]!, cell);
      const [bx, by] = crossPoint(eb, q[eb]!, cell);
      for (let y = 0; y < cell; y++) {
        for (let x = 0; x < cell; x++) {
          if (distToSegment(x + 0.5, y + 0.5, ax, ay, bx, by) <= r) put(rgba, w, tx + x, ty + y);
        }
      }
    }
  }
  return { cell, count: BANK.count, cols, w, h, rgba };
}
